export type ShiftsDefaultView = 'list' | 'calendar';

const LEGACY_KEY = 'shiftsDefaultView';

function storageKey(userId: number | string) {
  return `shiftsDefaultView:user:${userId}`;
}

function normalizeView(value: unknown): ShiftsDefaultView | null {
  return value === 'list' || value === 'calendar' ? value : null;
}

function getCurrentUserId(): number | null {
  try {
    const raw = localStorage.getItem('user');
    if (!raw) return null;
    const user = JSON.parse(raw);
    return typeof user?.id === 'number' ? user.id : null;
  } catch {
    return null;
  }
}

/** Read per-user preference (local cache). Falls back to legacy key once, then defaults to list. */
export function getShiftsDefaultView(userId?: number | null): ShiftsDefaultView {
  try {
    const id = userId ?? getCurrentUserId();
    if (id != null) {
      const scoped = normalizeView(localStorage.getItem(storageKey(id)));
      if (scoped) return scoped;

      // One-time migrate from old global key into this user's slot
      const legacy = normalizeView(localStorage.getItem(LEGACY_KEY));
      if (legacy) {
        localStorage.setItem(storageKey(id), legacy);
        localStorage.removeItem(LEGACY_KEY);
        return legacy;
      }
    } else {
      const legacy = normalizeView(localStorage.getItem(LEGACY_KEY));
      if (legacy) return legacy;
    }
  } catch {
    /* ignore */
  }
  return 'list';
}

export function setShiftsDefaultView(view: ShiftsDefaultView, userId?: number | null) {
  const id = userId ?? getCurrentUserId();
  if (id == null) {
    localStorage.setItem(LEGACY_KEY, view);
    return;
  }
  localStorage.setItem(storageKey(id), view);
  try {
    localStorage.removeItem(LEGACY_KEY);
  } catch {
    /* ignore */
  }
}

function personalCalKey(userId: number | string) {
  return `showPersonalCalendarEvents:user:${userId}`;
}

/** Whether to show personal calendar events on Shifts / My Shifts (default true). */
export function getShowPersonalCalendarEvents(userId?: number | null): boolean {
  try {
    const id = userId ?? getCurrentUserId();
    if (id == null) return true;
    const raw = localStorage.getItem(personalCalKey(id));
    if (raw === '0' || raw === 'false') return false;
    if (raw === '1' || raw === 'true') return true;
  } catch {
    /* ignore */
  }
  return true;
}

export function setShowPersonalCalendarEvents(show: boolean, userId?: number | null) {
  const id = userId ?? getCurrentUserId();
  if (id == null) return;
  localStorage.setItem(personalCalKey(id), show ? '1' : '0');
}

export type PreferredLanguage = 'cs' | 'en';

const LANGUAGE_LEGACY_KEY = 'language';

function languageStorageKey(userId: number | string) {
  return `language:user:${userId}`;
}

function normalizeLanguage(value: unknown): PreferredLanguage | null {
  return value === 'cs' || value === 'en' ? value : null;
}

/**
 * Read language for a specific user only.
 * Never falls back to the global `language` key for logged-in users
 * (that would share one language across all accounts in the browser).
 */
export function getPreferredLanguage(userId?: number | null): PreferredLanguage {
  try {
    const id = userId ?? getCurrentUserId();
    if (id != null) {
      const scoped = normalizeLanguage(localStorage.getItem(languageStorageKey(id)));
      if (scoped) return scoped;

      // Prefer value from the current user object (from login /me)
      try {
        const raw = localStorage.getItem('user');
        if (raw) {
          const user = JSON.parse(raw);
          if (user?.id === id) {
            const fromUser = normalizeLanguage(user.preferredLanguage);
            if (fromUser) {
              localStorage.setItem(languageStorageKey(id), fromUser);
              return fromUser;
            }
          }
        }
      } catch {
        /* ignore */
      }

      return 'en';
    }

    // Anonymous / marketing pages only
    const legacy = normalizeLanguage(localStorage.getItem(LANGUAGE_LEGACY_KEY));
    if (legacy) return legacy;
  } catch {
    /* ignore */
  }
  return 'en';
}

export function setPreferredLanguage(lang: PreferredLanguage, userId?: number | null) {
  const id = userId ?? getCurrentUserId();
  if (id == null) {
    localStorage.setItem(LANGUAGE_LEGACY_KEY, lang);
    return;
  }
  localStorage.setItem(languageStorageKey(id), lang);
  // Stop sharing a single language across accounts
  try {
    localStorage.removeItem(LANGUAGE_LEGACY_KEY);
  } catch {
    /* ignore */
  }
}

/** Apply a logged-in user's language into scoped storage + notify UI. */
export function applyUserLanguagePreference(
  user: { id?: number; preferredLanguage?: unknown } | null | undefined
) {
  if (!user?.id) return;
  const lang = normalizeLanguage(user.preferredLanguage) || getPreferredLanguage(user.id);
  setPreferredLanguage(lang, user.id);
  window.dispatchEvent(new CustomEvent('languagePreferenceUpdated', { detail: lang }));
}

export type TourProgressStatus = 'completed' | 'skipped';

export type TourProgressEntry = {
  status: TourProgressStatus;
  at: string;
  lastStep?: number;
  /** Flow control: guided | discover | done (used on *.flow keys) */
  mode?: 'guided' | 'discover' | 'done';
};

export type TourProgressMap = Record<string, TourProgressEntry>;

function tourProgressKey(userId: number | string) {
  return `tourProgress:user:${userId}`;
}

function normalizeTourProgress(value: unknown): TourProgressMap {
  if (!value) return {};
  if (typeof value === 'string') {
    try {
      return normalizeTourProgress(JSON.parse(value));
    } catch {
      return {};
    }
  }
  if (typeof value !== 'object' || Array.isArray(value)) return {};
  const out: TourProgressMap = {};
  for (const [tourId, entry] of Object.entries(value as Record<string, unknown>)) {
    if (!entry || typeof entry !== 'object' || Array.isArray(entry)) continue;
    const e = entry as Record<string, unknown>;
    if (e.status !== 'completed' && e.status !== 'skipped') continue;
    out[tourId] = {
      status: e.status,
      at: typeof e.at === 'string' ? e.at : new Date().toISOString(),
      ...(typeof e.lastStep === 'number' ? { lastStep: e.lastStep } : {}),
      ...(e.mode === 'guided' || e.mode === 'discover' || e.mode === 'done'
        ? { mode: e.mode }
        : {}),
    };
  }
  return out;
}

export function getTourProgress(userId?: number | null): TourProgressMap {
  try {
    const id = userId ?? getCurrentUserId();
    if (id == null) return {};
    const raw = localStorage.getItem(tourProgressKey(id));
    if (raw) return normalizeTourProgress(raw);
    try {
      const userRaw = localStorage.getItem('user');
      if (userRaw) {
        const user = JSON.parse(userRaw);
        if (user?.id === id && user.tourProgress) {
          const fromUser = normalizeTourProgress(user.tourProgress);
          setTourProgress(fromUser, id);
          return fromUser;
        }
      }
    } catch {
      /* ignore */
    }
  } catch {
    /* ignore */
  }
  return {};
}

export function setTourProgress(progress: TourProgressMap, userId?: number | null) {
  const id = userId ?? getCurrentUserId();
  if (id == null) return;
  localStorage.setItem(tourProgressKey(id), JSON.stringify(progress));
}

export function isTourDone(tourId: string, userId?: number | null): boolean {
  const entry = getTourProgress(userId)[tourId];
  return entry?.status === 'completed' || entry?.status === 'skipped';
}

export function applyUserTourProgress(
  user: { id?: number; tourProgress?: unknown } | null | undefined
) {
  if (!user?.id) return;
  const progress = normalizeTourProgress(user.tourProgress);
  setTourProgress(progress, user.id);
}
